import { routerNavigatedAction } from '@ngrx/router-store';
import { on } from '@ngrx/store';

import { FIELD_CONFIG, JOB_ROLE_MAP } from 'src/app/global.constants';

import { NewOpportunityActions } from 'src/app/new-opportunity/new-opportunity.actions';

import { NewOpportunityCustomerActions } from '../../../new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { unifyNumberFormat, getCustomerInfo, getJobCustomerInfo } from '../../job-helpers';
import { JobToolActions } from '../../job-tool.actions';

import { DiscardOpportunityDialogActions } from '../discard-job-component/discard-job.actions';


import { JobCreateCustomerActions } from './job-create-customer.actions';
import { additionalContactInitialState, JobCreateCustomerState, jobCreateCustomerInitialState } from './job-create-customer.state';

export const DEFAULT_CUSTOMER_TYPE = 'Residential';

export const jobCreateCustomerReducers = [
    on(
      JobCreateCustomerActions.jobCustomerLoaded,
      (state: JobCreateCustomerState, { job }): JobCreateCustomerState => {

          // Populate customer input based on job customer
          const customerInfo = getJobCustomerInfo(job);

          // Populate how heard field based on job
          const howHeardField = job.fields.find((f) => f.name === FIELD_CONFIG.customer.howHeard.name);

          return {
              ...state,
              customerEditor: {
                  ...customerInfo,
                  customerType: job.metadata?.jobType,
                  jobOrigin: job.metadata?.jobOrigin,
                  referralSource: howHeardField?.values?.[0]?.value,
                  suggestedCustomers: {},
                  isCustomerModified: false,
                  focusedField: null,
              },
              isCustomerEditMode: true,
          };
        },
    ),
    on(
      JobCreateCustomerActions.cancelEditCustomerButtonClicked,
      JobToolActions.jobLoaded,
      NewOpportunityActions.unsavedChangesCleared,
      (state: JobCreateCustomerState): JobCreateCustomerState => {
          return {
              ...state,
              ...jobCreateCustomerInitialState,
          };
        },
    ),
    on(JobCreateCustomerActions.searchUsersSuccess, (state: JobCreateCustomerState, res): JobCreateCustomerState => {
        const mappedUsers = res.users.map((u) => ({
            ...u,
            fullName: `${u.givenName} ${u.familyName}`.trim(),
        }));
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                suggestedCustomers: {
                    ...state.customerEditor.suggestedCustomers,
                    [res.field]: mappedUsers,
                },
            },
        }
    }),
    on(
      JobCreateCustomerActions.customerAutocompleteInputFocused,
      NewOpportunityCustomerActions.customerAutocompleteInputFocused,
      (state: JobCreateCustomerState, { field }): JobCreateCustomerState => {
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                focusedField: field,
            },
        };
    }),
    on(
      JobCreateCustomerActions.customerAutocompleteInputBlur,
      NewOpportunityCustomerActions.customerAutocompleteInputBlur,
      (state: JobCreateCustomerState, { field }): JobCreateCustomerState => {
        // Only clear focused field if it matches the blurred field
        if (state.customerEditor.focusedField !== field) {
            return state;
        }
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                focusedField: null,
            },
        };
    }),
    on(
      JobCreateCustomerActions.existingCustomerSelected,
      JobCreateCustomerActions.existingCustomerPreSelected,
      NewOpportunityCustomerActions.existingCustomerSelected,
      (state: JobCreateCustomerState, res): JobCreateCustomerState => {

        const customerInfo = getCustomerInfo(res.existingCustomer);

        return {
          ...state,
          customerEditor: {
            ...state.customerEditor,
            ...customerInfo,
            isCustomerModified: false,
          },
        };
        },
    ),

    on(
      JobCreateCustomerActions.customerAutocompleteInputChanged,
      NewOpportunityCustomerActions.customerAutocompleteInputChanged,
      (state: JobCreateCustomerState, res): JobCreateCustomerState => {
        if (res.key === 'Tab' || res.key === 'Shift') {
            return state;
        }

        const rawValue = res.value ?? '';
        const hasValue = Boolean(rawValue.trim());

        if (!hasValue && res.field === 'fullName') {
          return {
            ...state,
            customerEditor: {
              ...state.customerEditor,
              [res.field]: '',
              // when fullName of selected existing customer is cleared,
              // keep rest information to create new customer
              selectedCustomer: undefined,
              isCustomerModified: false,
            },
          };
        }

        let value = '';
        if (hasValue) {
          value = res.field === 'phone' ? unifyNumberFormat(rawValue) : rawValue;
        }

        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                [res.field]: value,
                isCustomerModified: true,
            },
        };
    }),
    on(
      NewOpportunityCustomerActions.fullNameInputChanged,
      (state: JobCreateCustomerState, { value }): JobCreateCustomerState => {

        const rawValue = value ?? '';
        const hasValue = Boolean(rawValue.trim());

        if (!hasValue) {
          return {
            ...state,
            customerEditor: {
              ...state.customerEditor,
              fullName: '',
              selectedCustomer: undefined,
              isCustomerModified: false,
            },
          };
        }

        return {
          ...state,
          customerEditor: {
            ...state.customerEditor,
            fullName: rawValue,
            isCustomerModified: true,
          },
        };
      },
    ),
    on(
      NewOpportunityCustomerActions.emailInputChanged,
      (state: JobCreateCustomerState, { value }): JobCreateCustomerState => {

        // Spaces are never valid in an email; a pasted leading/trailing space is
        // invisible in the input but fails EMAIL_PATTERN on save
        const email = (value ?? '').trim();

        return {
          ...state,
          customerEditor: {
            ...state.customerEditor,
            email,
            isCustomerModified: true,
          },
        };
      },
    ),
    on(
      NewOpportunityCustomerActions.phoneInputChanged,
      (state: JobCreateCustomerState, { value }): JobCreateCustomerState => {

        const rawValue = value ?? '';
        const phone = rawValue.trim() ? unifyNumberFormat(rawValue) : '';

        return {
          ...state,
          customerEditor: {
            ...state.customerEditor,
            phone,
            isCustomerModified: true,
          },
        };
      },
    ),
    on(
      NewOpportunityCustomerActions.companyInputChanged,
      (state: JobCreateCustomerState, { value }): JobCreateCustomerState => {

        const rawValue = value ?? '';
        const company = rawValue.trim() ? rawValue : '';

        return {
          ...state,
          customerEditor: {
            ...state.customerEditor,
            company,
            isCustomerModified: true,
          },
        };
      },
    ),
    on(
      JobCreateCustomerActions.clearCustomerButtonClicked,
      NewOpportunityCustomerActions.clearCustomerButtonClicked,
      (state: JobCreateCustomerState): JobCreateCustomerState => {
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                ...getCustomerInfo(),
                isCustomerModified: false,
            },
        }
    }),
    on(
        JobCreateCustomerActions.addAdditionalContactButtonClicked,
        NewOpportunityCustomerActions.addAdditionalContactButtonClicked,
        (state: JobCreateCustomerState): JobCreateCustomerState => ({
            ...state,
            customerEditor: {
                ...state.customerEditor,
                additionalContacts: [
                    ...state.customerEditor.additionalContacts,
                    { ...additionalContactInitialState },
                ],
                isCustomerModified: true,
            },
        })
    ),
    on(
        JobCreateCustomerActions.additionalContactFieldChanged,
        NewOpportunityCustomerActions.additionalContactFieldChanged,
        (state: JobCreateCustomerState, { index, field, value }): JobCreateCustomerState => {
            const contacts = state.customerEditor.additionalContacts;

            if (index < 0 || index >= contacts.length) {
                return state;
            }

            const normalizedValue = field === 'phone' ? unifyNumberFormat(value) : value.trim();

            return {
                ...state,
                customerEditor: {
                    ...state.customerEditor,
                    additionalContacts: contacts.map((c, i) =>
                        i === index
                            ? { ...c, [field]: normalizedValue, isContactModified: true }
                            : c
                    ),
                    isCustomerModified: true,
                },
            };
        }
    ),
    on(
        JobCreateCustomerActions.removeAdditionalContactButtonClicked,
        NewOpportunityCustomerActions.removeAdditionalContactButtonClicked,
        (state: JobCreateCustomerState, { index }): JobCreateCustomerState => {
            const contacts = state.customerEditor.additionalContacts;
            const contact = contacts[index];

            if (contact?.userId) {
                return {
                    ...state,
                    customerEditor: {
                        ...state.customerEditor,
                        additionalContacts: contacts.map((c, i) =>
                            i === index ? { ...c, isUnlinked: true } : c
                        ),
                        isCustomerModified: true,
                    },
                };
            }

            return {
                ...state,
                customerEditor: {
                    ...state.customerEditor,
                    additionalContacts: contacts.filter((_, i) => i !== index),
                    isCustomerModified: true,
                },
            };
        }
    ),
    on(
        JobCreateCustomerActions.existingAdditionalContactSelected,
        NewOpportunityCustomerActions.existingAdditionalContactSelected,
        (state: JobCreateCustomerState, { index, existingContact }): JobCreateCustomerState => {
            const contacts = state.customerEditor.additionalContacts;

            if (index < 0 || index >= contacts.length) {
                return state;
            }

            return {
                ...state,
                customerEditor: {
                    ...state.customerEditor,
                    additionalContacts: contacts.map((c, i) =>
                        i === index
                            ? {
                                ...c,
                                selectedAdditionalContact: existingContact,
                                name: `${existingContact.givenName ?? ''} ${existingContact.familyName ?? ''}`.trim(),
                                email: existingContact.email ?? '',
                                phone: existingContact.phone ?? '',
                                isContactModified: true,
                              }
                            : c
                    ),
                    isCustomerModified: true,
                },
            };
        }
    ),
    on(
      JobCreateCustomerActions.referralSourceSelected,
      NewOpportunityCustomerActions.referralSourceSelected,
      (state: JobCreateCustomerState, { referralSource }): JobCreateCustomerState => {
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                referralSource,
            },
        };
    }),
    on(
      JobCreateCustomerActions.customerTypeSelected,
      NewOpportunityCustomerActions.customerTypeSelected,
      (state: JobCreateCustomerState, { customerType }): JobCreateCustomerState => {
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                customerType,
            }
        };
    }),
    on(
      JobCreateCustomerActions.jobOriginSelected,
      NewOpportunityCustomerActions.jobOriginSelected,
      (state: JobCreateCustomerState, { jobOrigin }): JobCreateCustomerState => {
        return {
            ...state,
            customerEditor: {
                ...state.customerEditor,
                jobOrigin,
            }
        };
    }),
    on(JobCreateCustomerActions.acceptCustomerChanges, (state: JobCreateCustomerState): JobCreateCustomerState => {
        // We deactivate edit mode as soon as user clicks "save"
        // but we do *not* clear the inputs yet as mutation may fail
        return {
            ...state,
            isCustomerEditMode: false,
        };
    }),
    on(
      JobCreateCustomerActions.updateCustomerDataSuccess,
      (state: JobCreateCustomerState): JobCreateCustomerState => {
          return {
              ...state,
              ...jobCreateCustomerInitialState,
          }
        },
    ),
    on(JobCreateCustomerActions.updateCustomerDataError, (state: JobCreateCustomerState): JobCreateCustomerState => {
        return {
            ...state,
            isCustomerEditMode: true,
        }; }),
    on(DiscardOpportunityDialogActions.discardUnsavedChangesButtonClicked, (): JobCreateCustomerState => jobCreateCustomerInitialState),
    on(JobToolActions.saveChangesSuccess, (state: JobCreateCustomerState, { updatedJob, customerEdit }): JobCreateCustomerState => {

      // No user updates
      if (!customerEdit) {
        return state;
      }

      // Existing customer was selected, meaning no new user was created
      if (customerEdit.selectedCustomer) {
        return state;
      }

      // auto-select new customer user
      const newCustomerUser = updatedJob?.users?.find((u) => u.role === JOB_ROLE_MAP.customerRole)?.user;

      return {
        ...state,
        customerEditor: {
          ...state.customerEditor,
          selectedCustomer: newCustomerUser,
        }
      }
    }),
    on(routerNavigatedAction, (state: JobCreateCustomerState, { payload: { event: { urlAfterRedirects } } }): JobCreateCustomerState => {
      if (urlAfterRedirects.startsWith('/jobs/new')) {
            return jobCreateCustomerInitialState;
        }
        return state;
    }),
    on(NewOpportunityActions.defaultCustomerTypeLoaded, (state: JobCreateCustomerState): JobCreateCustomerState => ({
        ...state,
        customerEditor: {
          ...state.customerEditor,
          customerType: DEFAULT_CUSTOMER_TYPE,
        }
    })),
] as const;
